import { Sheet, SheetContent, SheetTitle } from '@iptv/ui';
import {
  Activity,
  BarChart3,
  CreditCard,
  FileText,
  Gift,
  Image,
  LayoutDashboard,
  LogOut,
  Menu,
  Settings,
  Tag,
  Tv,
  Users,
  Wallet,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

import { logout, useCurrentAdmin } from '../lib/auth';

import { GlobalSearch } from './GlobalSearch';

const NAV: { to: string; label: string; icon: React.ReactNode }[] = [
  { to: '/', label: 'Dashboard', icon: <LayoutDashboard className="h-4 w-4" /> },
  { to: '/orders', label: 'Orders', icon: <Wallet className="h-4 w-4" /> },
  { to: '/leads', label: 'Leads', icon: <Users className="h-4 w-4" /> },
  { to: '/subscriptions', label: 'Subscriptions', icon: <CreditCard className="h-4 w-4" /> },
  { to: '/metrics', label: 'Metrics', icon: <BarChart3 className="h-4 w-4" /> },
  { to: '/sites', label: 'Sites', icon: <Tv className="h-4 w-4" /> },
  { to: '/plans', label: 'Plans', icon: <Tag className="h-4 w-4" /> },
  { to: '/bumps', label: 'Bumps', icon: <Gift className="h-4 w-4" /> },
  { to: '/coupons', label: 'Coupons', icon: <Tag className="h-4 w-4" /> },
  { to: '/assets', label: 'Assets', icon: <Image className="h-4 w-4" /> },
  { to: '/audit', label: 'Audit log', icon: <FileText className="h-4 w-4" /> },
  { to: '/settings', label: 'Settings', icon: <Settings className="h-4 w-4" /> },
];

/**
 * Sidebar body shared by the persistent desktop `<aside>` and the mobile
 * slide-over drawer. `onNavigate` lets the drawer close itself when a link
 * is tapped.
 */
function SidebarBody({
  email,
  role,
  onNavigate,
}: {
  email?: string | null;
  role?: string | null;
  onNavigate?: () => void;
}) {
  return (
    <>
      <div className="flex h-14 items-center gap-2 border-b px-4">
        <Activity className="h-5 w-5 text-primary" />
        <span className="text-lg font-semibold">IPTV Admin</span>
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto px-2 py-4">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                isActive
                  ? 'bg-secondary text-secondary-foreground'
                  : 'text-muted-foreground hover:bg-secondary/50 hover:text-foreground'
              }`
            }
          >
            {item.icon}
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t p-4">
        <div className="mb-2 truncate text-sm font-medium">{email ?? '…'}</div>
        <div className="mb-3 text-xs capitalize text-muted-foreground">
          {/*
            `?.` per hop on purpose: `role` can be absent on older `/auth/me`
            responses (accounts mid-migration). `.replace` on undefined would
            throw and crash AppLayout, which wraps every authenticated page.
          */}
          {role?.replace('_', ' ') ?? ''}
        </div>
        <button
          type="button"
          onClick={logout}
          className="flex w-full items-center gap-2 rounded-md border px-3 py-1.5 text-sm hover:bg-secondary"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </button>
      </div>
    </>
  );
}

export function AppLayout() {
  const me = useCurrentAdmin();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const current = NAV.find((n) =>
    n.to === '/' ? loc.pathname === '/' : loc.pathname.startsWith(n.to),
  );

  // Close the mobile nav drawer whenever the route changes.
  useEffect(() => {
    setMobileOpen(false);
  }, [loc.pathname]);

  return (
    <div className="flex h-screen bg-background text-foreground">
      {/* Sidebar — persistent on desktop (md+) */}
      <aside className="hidden w-64 flex-col border-r bg-card md:flex">
        <SidebarBody email={me.data?.email} role={me.data?.role} />
      </aside>

      {/* Sidebar — slide-over drawer on mobile */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="flex w-72 max-w-[85vw] flex-col p-0">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SidebarBody
            email={me.data?.email}
            role={me.data?.role}
            onNavigate={() => setMobileOpen(false)}
          />
        </SheetContent>
      </Sheet>

      <main className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="flex h-14 items-center gap-3 border-b px-4 md:px-6">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="-ml-1 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground md:hidden"
            aria-label="Open navigation menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          <h1 className="min-w-0 flex-1 truncate text-lg font-semibold">
            {current?.label ?? 'Dashboard'}
          </h1>
          <GlobalSearch />
        </header>
        <div className="flex-1 overflow-auto p-4 md:p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
